import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Cpu, Gauge, Sparkles, Target } from 'lucide-react';
import { cn } from '../ui/utils';
import type { AdaptiveProfile, PriorityTool } from '../../lib/adaptive-experience';

type Props = {
  profile: AdaptiveProfile;
  onJumpToSection: (sectionId: string) => void;
  syncHint?: string | null;
  className?: string;
};

function focusBadge(focus: AdaptiveProfile['focus']) {
  const labels: Record<AdaptiveProfile['focus'], string> = {
    repair: 'Repair',
    communication: 'Communication',
    patterns: 'Patterns',
    connection: 'Connection',
    balanced: 'Balanced',
  };
  return labels[focus];
}

export function IntelligenceBriefing({ profile, onJumpToSection, syncHint, className }: Props) {
  return (
    <Card
      className={cn(
        'tension-glow relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-950/40 via-neutral-950/90 to-violet-950/50 p-6 text-neutral-100 shadow-xl backdrop-blur-sm',
        className,
      )}
    >
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-cyan-500/15 blur-3xl"
        aria-hidden
      />
      <div className="relative space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-950/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-200">
            <Cpu className="h-3.5 w-3.5" aria-hidden />
            Adaptive engine
          </span>
          <Badge variant="outline" className="border-violet-400/40 bg-violet-950/40 text-violet-100">
            <Target className="mr-1 h-3 w-3" aria-hidden />
            {focusBadge(profile.focus)}
          </Badge>
          <span className="inline-flex items-center gap-1 text-xs text-cyan-200/90">
            <Gauge className="h-3.5 w-3.5" aria-hidden />
            Signal {Math.round(profile.signalStrength * 100)}% · Data {profile.dataQuality.label} (
            {profile.dataQuality.score}/100)
          </span>
        </div>

        <div>
          <h2 className="text-xl font-bold tracking-tight text-white md:text-2xl">{profile.headline}</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-cyan-100/85">{profile.subline}</p>
        </div>

        {profile.enterpriseNotes.length > 0 && (
          <ul className="space-y-1 text-xs text-cyan-100/80">
            {profile.enterpriseNotes.map((n) => (
              <li key={n} className="flex gap-2">
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        )}

        {syncHint && (
          <p className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-neutral-300">{syncHint}</p>
        )}

        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Jump to your priorities</p>
          <div className="flex flex-wrap gap-2">
            {profile.priorityTools.map((t: PriorityTool) => (
              <Button
                key={t.sectionId}
                type="button"
                size="sm"
                variant="secondary"
                className="h-auto border border-white/10 bg-white/10 py-2 text-left text-xs text-white hover:bg-white/15"
                onClick={() => onJumpToSection(t.sectionId)}
              >
                <span className="font-semibold">{t.label}</span>
                <span className="mt-0.5 block text-[10px] font-normal text-neutral-300">{t.reason}</span>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
